import { FileWarning, Lock } from 'lucide-react'

import { Button, ExplainDisabled } from '@/components/ui/button'
import { Badge } from '@/components/ui/misc'
import { Dialog } from '@/components/ui/overlay'
import { formatBytes, formatDate } from '@/lib/format'
import { useUploads } from './UploadProvider'

/**
 * Resolves upload name collisions before any bytes are sent.
 *
 * This step exists because HCP is not AWS S3: on a namespace without
 * versioning, writing a key that already exists is an error rather than an
 * overwrite. Asking here means a 2 GB upload never fails at 100%.
 */
export function ConflictDialog() {
  const { items, resolve, resolveAll, hasConflicts } = useUploads()
  const conflicts = items.filter((item) => item.status === 'conflict')

  if (!hasConflicts) return null

  const anyReplaceable = conflicts.some((item) => !item.conflict?.locked)

  return (
    <Dialog
      open
      size="lg"
      onClose={() => resolveAll('skip')}
      title={conflicts.length === 1 ? 'A file with this name already exists' : `${conflicts.length} files already exist`}
      description={
        <>
          This namespace is not versioned, so an existing object cannot simply be overwritten.
          Choose what to do with each file.
        </>
      }
      footer={
        <>
          <Button onClick={() => resolveAll('skip')}>Skip all</Button>
          <Button onClick={() => resolveAll('rename')}>Keep both for all</Button>
          {anyReplaceable && (
            <Button variant="danger" onClick={() => resolveAll('replace')}>
              Replace all possible
            </Button>
          )}
        </>
      }
    >
      <ul className="space-y-3">
        {conflicts.map((item) => {
          const locked = item.conflict?.locked ?? false
          return (
            <li
              key={item.id}
              className="rounded-md border border-border bg-surface px-4 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <p className="flex items-center gap-2 truncate font-medium">
                    <FileWarning className="h-4 w-4 shrink-0 text-warning" />
                    <span className="truncate">{item.key}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Existing: {formatBytes(item.conflict?.existingSize ?? 0)} ·{' '}
                    {formatDate(item.conflict?.existingModified ?? null)}
                    {' — new: '}
                    {formatBytes(item.totalBytes)}
                  </p>
                  {locked && (
                    <Badge tone="danger" title={item.conflict?.lockReason ?? undefined}>
                      <Lock className="h-3 w-3" />
                      {item.conflict?.lockReason ?? 'Protected by retention'}
                    </Badge>
                  )}
                </div>

                <div className="flex shrink-0 gap-2">
                  <Button size="sm" onClick={() => resolve(item.id, 'skip')}>
                    Skip
                  </Button>
                  <Button size="sm" onClick={() => resolve(item.id, 'rename')}>
                    Keep both
                  </Button>
                  <ExplainDisabled
                    reason={
                      locked
                        ? `Cannot replace: ${item.conflict?.lockReason ?? 'protected by retention'}. HCP will not delete a retained object.`
                        : null
                    }
                  >
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={locked}
                      onClick={() => resolve(item.id, 'replace')}
                    >
                      Replace
                    </Button>
                  </ExplainDisabled>
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <p className="mt-4 text-xs text-muted-foreground">
        <strong>Keep both</strong> uploads under a new name such as{' '}
        <code className="font-mono">report (2).pdf</code>. <strong>Replace</strong> deletes the
        existing object only once every byte of the new one has been received — but the two steps
        cannot be atomic on WORM storage, so the original is briefly gone before the replacement
        lands.
      </p>
    </Dialog>
  )
}
